export type VolumeIcon = 'wrench' | 'gauge' | 'disc' | 'zap' | 'cog' | 'monitor'

export interface Volume {
  icon: VolumeIcon
  number: string
  title: string
  subtitle: string
  problem: string[]
  bulletsIntro?: string
  bullets: string[]
  result: string
  resultChain?: string
  resultAfter?: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    icon: 'wrench',
    number: 'Volume 1',
    title: 'Automotive Foundations, Workshop Safety & Service Procedures',
    subtitle: 'Build the professional foundation behind every automotive lesson',
    problem: [
      'Before students can understand diagnostics, electrical systems or advanced vehicle technology, they need strong workshop fundamentals.',
      'This volume gives you a structured way to teach those foundations without creating every introductory lesson yourself.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Automotive workshop orientation',
      'Workshop safety',
      'PPE fundamentals',
      'Hand and power tools',
      'Vehicle lifting and support awareness',
      'Fasteners and torque concepts',
      'Fluids and lubricants',
      'Basic inspections',
      'Preventive maintenance',
      'Service-information awareness',
      'Workshop organization',
      'Vehicle identification',
      'Practical service procedures',
      'Instructor observation points',
      'Ready-to-teach lesson structures',
    ],
    result:
      'Your students develop stronger workshop habits and understand the essential procedures and concepts required before progressing into more advanced automotive systems.',
  },
  {
    icon: 'gauge',
    number: 'Volume 2',
    title: 'Engine Systems, Performance & Maintenance',
    subtitle: 'Teach how the engine works — not just the names of the parts',
    problem: [
      'Students often memorize components without understanding how combustion, airflow, fuel, ignition, timing, lubrication and cooling work together.',
      'This volume helps you teach the engine as one complete interacting system.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Four-stroke cycle',
      'Engine architecture',
      'Cylinder head',
      'Pistons',
      'Connecting rods',
      'Crankshaft',
      'Camshaft',
      'Valve train',
      'Timing systems',
      'Compression',
      'Lubrication',
      'Cooling',
      'Intake',
      'Exhaust',
      'Fuel delivery',
      'Ignition',
      'Emissions fundamentals',
      'Engine-performance relationships',
      'Inspection concepts',
      'Testing fundamentals',
      'Common symptom relationships',
      'Visual system flows',
      'Component interaction diagrams',
    ],
    result:
      'Students stop seeing the engine as disconnected individual parts and start understanding how the complete system works together to produce power.',
  },
  {
    icon: 'disc',
    number: 'Volume 3',
    title: 'Brakes, Steering, Suspension & Chassis',
    subtitle: 'Turn chassis systems into visual lessons students can actually understand',
    problem: [
      'Braking, steering and suspension become difficult when students cannot visualize force, movement, geometry and component relationships.',
      'This volume makes those relationships easier to see and teach.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Hydraulic brake fundamentals',
      'Master cylinder operation',
      'Disc brakes',
      'Drum brake concepts',
      'Brake boosters',
      'ABS fundamentals',
      'Brake inspection',
      'Rack-and-pinion steering',
      'Hydraulic power steering',
      'Electric power steering',
      'Steering linkage',
      'Suspension architectures',
      'Springs',
      'Shocks',
      'Struts',
      'Control arms',
      'Ball joints',
      'Bushings',
      'Stabilizer bars',
      'Wheel bearings',
      'Tires',
      'Alignment fundamentals',
      'Chassis inspection',
      'Correct-vs-incorrect comparisons',
    ],
    result:
      'Students can visualize how vehicle control, braking force, steering input and suspension movement work together instead of relying only on theory.',
  },
  {
    icon: 'zap',
    number: 'Volume 4',
    title: 'Automotive Electrical & Electronic Systems',
    subtitle: 'Make automotive electricity easier to see, explain and diagnose',
    problem: [
      'Electrical systems are difficult because students cannot physically see voltage, current, resistance or electronic signals.',
      'This volume turns those invisible concepts into 100% visual teaching material.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Voltage',
      'Current',
      'Resistance',
      'Ohm’s Law',
      'Series circuits',
      'Parallel circuits',
      'Power',
      'Ground',
      'Wiring diagrams',
      'Fuses',
      'Relays',
      'Connectors',
      'Grounds',
      'Sensors',
      'Actuators',
      'Digital multimeter use',
      'Voltage-drop concepts',
      'Battery systems',
      'Starting systems',
      'Charging systems',
      'Lighting systems',
      'Control modules',
      'Circuit troubleshooting',
      'Input → Module → Output relationships',
    ],
    result:
      'Instead of memorizing electrical definitions, students can see how circuits operate, where measurements are taken and how faults are logically isolated.',
  },
  {
    icon: 'cog',
    number: 'Volume 5',
    title: 'Transmissions, Drivetrain & Power Delivery',
    subtitle: 'Show exactly how engine power reaches the wheels',
    problem: [
      'Students may understand the engine but still struggle to understand everything that happens between engine output and wheel movement.',
      'This volume makes that complete path visible.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Clutch fundamentals',
      'Manual transmissions',
      'Automatic transmissions',
      'Transaxles',
      'Torque converters',
      'Gear ratios',
      'Differentials',
      'Final drive',
      'Driveshafts',
      'CV joints',
      'Axles',
      'Front-wheel drive',
      'Rear-wheel drive',
      'AWD',
      '4WD fundamentals',
      'Transfer of torque',
      'Transmission-fluid concepts',
      'Drivetrain inspection',
      'Common symptom relationships',
      'Power-flow diagrams',
      'Component identification',
    ],
    result: 'Students understand the complete chain:',
    resultChain: 'ENGINE → TRANSMISSION → DRIVETRAIN → WHEELS',
    resultAfter: 'instead of treating every drivetrain component as an isolated topic.',
  },
  {
    icon: 'monitor',
    number: 'Volume 6',
    title: 'Diagnostics, Climate Control & Advanced Vehicle Technology',
    subtitle: 'Teach students how modern vehicle systems communicate, fail and get diagnosed',
    problem: [
      'Modern Automotive Technology goes far beyond mechanical component replacement.',
      'Students need to understand data, diagnostics, electronic control, communication networks and advanced vehicle technology.',
      'This volume gives you a 100% visual teaching framework for making those systems easier to explain.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Diagnostic workflow',
      'Symptom verification',
      'Scan-tool fundamentals',
      'OBD-II',
      'Diagnostic Trouble Codes',
      'Freeze-frame data',
      'Live data',
      'Sensor inputs',
      'Actuator outputs',
      'Power / ground / signal logic',
      'Electrical testing',
      'Voltage-drop concepts',
      'Diagnostic decision trees',
      'Misfire diagnosis',
      'Lean-condition logic',
      'EVAP fundamentals',
      'Automotive HVAC',
      'Refrigerant-cycle concepts',
      'ABS',
      'ESC',
      'SRS awareness',
      'Vehicle networks',
      'CAN communication',
      'Hybrid/EV fundamentals',
      'Regenerative braking',
      'ADAS fundamentals',
      'Cameras',
      'Radar',
      'Calibration awareness',
      'Diagnostic case studies',
    ],
    result:
      'You can take complex modern automotive technology and turn it into structured visual lessons where students can follow:',
    resultChain: 'SYMPTOM → DATA → TEST → RESULT → NEXT STEP',
    popular: true,
  },
]
